import { apiRequest, type AuthReplayClass } from "@/api/client";
import { env } from "@/app/env";
import type {
  AppRecord,
  AuditRecord,
  CertRecord,
  ClientLicensingRecord,
	CreateMonitoringTargetInput,
	CreateLocalMonitoringIntentInput,
	CustomerDirectoryInput,
	CustomerDirectoryItem,
	CustomerDirectoryPage,
	LocalCustomerMutationInput,
  LicensingClientFootprint,
  ClientProductEntitlementRecord,
  ClearStationAdminControlInput,
  CreateReleaseDownloadGrantInput,
  CreateReleaseInput,
  DeleteReleaseArtifactInput,
  DecommissionStationInput,
  EnrollmentTokenRecord,
  IssueEnrollmentTokenInput,
  IssueStationOverrideInput,
  IssuedEnrollmentTokenRecord,
  IssuedReleaseDownloadGrantRecord,
  ListResponse,
	MonitoringTargetPage,
	MonitoringTargetRecord,
	MonitoringTargetUpdateInput,
	MonitoringBindingMutationRecord,
	MonitoringBindingPage,
	RevokeMonitoringBindingInput,
	LocalMonitoringIntentRecord,
  PublicKeyRecord,
  ProductActivationMutationInput,
  ProductActivationRecord,
  ProductEntitlementMutationInput,
  RegistrationRequestApprovalInput,
  RegistrationRequestRecord,
  RegistrationRequestResolveRecord,
  RegistrationRequestResolveInput,
  ReleaseArtifactRecord,
  ReleaseDownloadGrantRecord,
  ReleaseRecord,
  ReleasePublicationManifest,
  ReleasePublicationRequestRecord,
  RestoreReleaseInput,
  RevokeReleaseDownloadGrantInput,
  RolloutReadinessReportRecord,
  SaveAppInput,
  StationAdminControlInput,
  StationLicenseStateRecord,
  StationOverrideRecord,
  StationRecord,
	TenantSession,
  OverrideUsageReportRecord,
  UpdateStationPolicyInput,
  UploadReleaseArtifactInput,
  YankReleaseInput
} from "@/api/types";

export const adminApiAuthReplayInventory = {
  listApps: "pure-safe-retrieval",
  saveApp: "never-replay",
  listReleases: "pure-safe-retrieval",
  createRelease: "never-replay",
  deleteRelease: "never-replay",
  listReleaseArtifacts: "pure-safe-retrieval",
  listReleaseDownloadGrants: "pure-safe-retrieval",
  createReleaseDownloadGrant: "never-replay",
  revokeReleaseDownloadGrant: "never-replay",
  uploadReleaseArtifact: "never-replay",
  deleteReleaseArtifact: "never-replay",
  publishRelease: "never-replay",
  yankRelease: "never-replay",
  restoreRelease: "never-replay",
  listEnrollmentTokens: "pure-safe-retrieval",
  issueEnrollmentToken: "never-replay",
  resolveRegistrationRequest: "retryable-idempotent-materializing-lookup",
  getRegistrationRequest: "retryable-idempotent-materializing-lookup",
  approveRegistrationRequest: "never-replay",
  listStations: "pure-safe-retrieval",
  getStation: "pure-safe-retrieval",
  getStationLicenseState: "pure-safe-retrieval",
  listStationCerts: "pure-safe-retrieval",
  listStationOverrides: "pure-safe-retrieval",
  issueStationOverride: "never-replay",
  decommissionStation: "never-replay",
  adminBanStation: "never-replay",
  adminAllowStation: "never-replay",
  clearAdminBanStation: "never-replay",
  clearAdminAllowStation: "never-replay",
  updateStationPolicy: "never-replay",
  listStationAudit: "pure-safe-retrieval",
  listAudit: "pure-safe-retrieval",
  getRolloutReadinessReport: "pure-safe-retrieval",
  getOverrideUsageReport: "pure-safe-retrieval",
  getPublicKey: "pure-safe-retrieval",
  createApprovedReleasePublication: "never-replay",
  listReleasePublicationRequests: "pure-safe-retrieval",
  resolveReleasePublicationRequest: "pure-safe-retrieval",
  approveReleasePublicationRequest: "never-replay",
  rejectReleasePublicationRequest: "never-replay",
  getClientLicensing: "pure-safe-retrieval",
	getTenantSession: "pure-safe-retrieval",
  listLicensingClientFootprint: "pure-safe-retrieval",
	listCustomerDirectory: "pure-safe-retrieval",
  getAuthorityCustomer: "pure-safe-retrieval",
	listAuthorityMonitoringTargets: "pure-safe-retrieval",
	listAuthorityMonitoringBindings: "pure-safe-retrieval",
	updateAuthorityMonitoringTarget: "server-idempotent-mutation",
	revokeAuthorityMonitoringBinding: "server-idempotent-mutation",
	createAuthorityMonitoringTarget: "server-idempotent-mutation",
	createLocalMonitoringIntent: "server-idempotent-mutation",
	createLocalCustomer: "server-idempotent-mutation",
	updateLocalCustomer: "server-idempotent-mutation",
  listProductActivations: "pure-safe-retrieval",
  allowProductActivation: "never-replay",
  rejectProductActivation: "never-replay",
  revokeProductActivation: "never-replay",
  suspendClientProductEntitlement: "never-replay",
  revokeClientProductEntitlement: "never-replay"
} as const satisfies Record<string, AuthReplayClass>;

type AdminApiOperation = keyof typeof adminApiAuthReplayInventory;

function adminApiRequest<T>(operation: AdminApiOperation, path: string, init?: RequestInit) {
	const surfacePath = path.replace(/^\/admin\/v1(?=\/|$)/, env.adminApiPrefix);
	return apiRequest<T>(surfacePath, adminApiAuthReplayInventory[operation], init);
}

export function listApps() {
  return adminApiRequest<ListResponse<AppRecord>>("listApps", "/admin/v1/apps");
}

export function getClientLicensing(clientId: number) {
  return adminApiRequest<ClientLicensingRecord>("getClientLicensing", `/admin/v1/clients/${clientId}/licensing`);
}

export function getTenantSession() {
	return adminApiRequest<TenantSession>("getTenantSession", "/admin/v1/session");
}

export function listLicensingClientFootprint() {
  return adminApiRequest<LicensingClientFootprint>(
    "listLicensingClientFootprint",
    "/admin/v1/clients/licensing-footprint"
  );
}

export function listCustomerDirectory(input: CustomerDirectoryInput) {
	const params = new URLSearchParams({ mode: input.mode, limit: String(input.limit ?? 24) });
	if (input.q?.trim()) params.set("q", input.q.trim());
	if (input.cursor) params.set("cursor", input.cursor);
	return adminApiRequest<CustomerDirectoryPage>(
		"listCustomerDirectory",
		`/admin/v1/customer-directory?${params.toString()}`
	);
}

export function getAuthorityCustomer(clientId: number) {
	return adminApiRequest<CustomerDirectoryItem>("getAuthorityCustomer", `/admin/v1/customers/${clientId}`);
}

export function createLocalCustomer(input: LocalCustomerMutationInput) {
	return adminApiRequest<CustomerDirectoryItem>("createLocalCustomer", "/admin/v1/customers", {
		method: "POST",
		body: JSON.stringify({ number: input.number, name: input.name, requestId: input.requestId })
	});
}

export function updateLocalCustomer(input: LocalCustomerMutationInput & { clientId: number; status: "ACTIVE" | "SUSPENDED" | "ARCHIVED"; expectedRevision: number }) {
	return adminApiRequest<CustomerDirectoryItem>("updateLocalCustomer", `/admin/v1/customers/${input.clientId}`, {
		method: "PATCH",
		body: JSON.stringify({
			number: input.number,
			name: input.name,
			status: input.status,
			expectedRevision: input.expectedRevision,
			requestId: input.requestId
		})
	});
}

export function listAuthorityMonitoringTargets(clientId: number, view: "all" | "attached" | "unbound" | "archived", cursor?: string) {
	const params = new URLSearchParams({ view });
	if (cursor) params.set("cursor", cursor);
	return adminApiRequest<MonitoringTargetPage>("listAuthorityMonitoringTargets", `/admin/v1/customers/${clientId}/monitoring-targets?${params.toString()}`);
}

export function updateAuthorityMonitoringTarget(clientId: number, input: MonitoringTargetUpdateInput) {
	return adminApiRequest<MonitoringTargetRecord>("updateAuthorityMonitoringTarget", `/admin/v1/customers/${clientId}/monitoring-targets/${encodeURIComponent(input.targetId)}`, {
		method: "PATCH",
		body: JSON.stringify({
			...(input.displayName === undefined ? {} : { display_name: input.displayName }),
			...(input.lifecycle === undefined ? {} : { lifecycle: input.lifecycle }),
			expected_version: input.expectedVersion,
			request_id: input.requestId
		})
	});
}

export function listAuthorityMonitoringBindings(clientId: number, targetId: string, cursor?: string) {
	const params = new URLSearchParams();
	if (cursor) params.set("cursor", cursor);
	const query = params.size ? `?${params.toString()}` : "";
	return adminApiRequest<MonitoringBindingPage>("listAuthorityMonitoringBindings", `/admin/v1/customers/${clientId}/monitoring-targets/${encodeURIComponent(targetId)}/bindings${query}`);
}

export function revokeAuthorityMonitoringBinding(clientId: number, input: RevokeMonitoringBindingInput) {
	return adminApiRequest<MonitoringBindingMutationRecord>("revokeAuthorityMonitoringBinding", `/admin/v1/customers/${clientId}/monitoring-targets/${encodeURIComponent(input.targetId)}/bindings/${encodeURIComponent(input.bindingId)}/revoke`, {
		method: "POST",
		body: JSON.stringify({
			expected_target_version: input.expectedTargetVersion,
			expected_binding_version: input.expectedBindingVersion,
			request_id: input.requestId
		})
	});
}

export function createAuthorityMonitoringTarget(input: CreateMonitoringTargetInput) {
	return adminApiRequest<MonitoringTargetRecord>("createAuthorityMonitoringTarget", `/admin/v1/customers/${input.clientId}/monitoring-targets`, {
		method: "POST",
		body: JSON.stringify({ type: input.type, displayName: input.displayName, requestId: input.requestId })
	});
}

export function createLocalMonitoringIntent(input: CreateLocalMonitoringIntentInput) {
	return adminApiRequest<LocalMonitoringIntentRecord>(
		"createLocalMonitoringIntent",
		`/admin/v1/customers/${input.clientId}/monitoring-enrollment-intents`,
		{
			method: "POST",
			body: JSON.stringify({
				requestId: input.requestId,
				relationKind: input.relationKind,
				relationId: input.relationId,
				...(input.targetId ? { targetId: input.targetId } : {}),
				...(input.createTargetType ? { createTargetType: input.createTargetType } : {}),
				...(input.createTargetName ? { createTargetName: input.createTargetName } : {})
			})
		}
	);
}

export function listProductActivations(params = new URLSearchParams()) {
  const query = params.toString();
  return adminApiRequest<ListResponse<ProductActivationRecord>>(
    "listProductActivations",
    `/admin/v1/product-activations${query ? `?${query}` : ""}`
  );
}

function mutateProductActivation(operation: "allowProductActivation" | "rejectProductActivation" | "revokeProductActivation", action: "allow" | "reject" | "revoke", input: ProductActivationMutationInput) {
  return adminApiRequest<ProductActivationRecord>(
    operation,
    `/admin/v1/clients/${input.clientId}/product-activations/${encodeURIComponent(input.requestId)}/${action}`,
    { method: "POST", body: JSON.stringify({ expectedRevision: input.expectedRevision, reason: input.reason }) }
  );
}

export function allowProductActivation(input: ProductActivationMutationInput) {
  return mutateProductActivation("allowProductActivation", "allow", input);
}

export function rejectProductActivation(input: ProductActivationMutationInput) {
  return mutateProductActivation("rejectProductActivation", "reject", input);
}

export function revokeProductActivation(input: ProductActivationMutationInput) {
  return mutateProductActivation("revokeProductActivation", "revoke", input);
}

function mutateClientProductEntitlement(operation: "suspendClientProductEntitlement" | "revokeClientProductEntitlement", action: "suspend" | "revoke", input: ProductEntitlementMutationInput) {
  return adminApiRequest<ClientProductEntitlementRecord>(
    operation,
    `/admin/v1/clients/${input.clientId}/products/${encodeURIComponent(input.appId)}/${action}`,
    { method: "POST", body: JSON.stringify({ expectedRevision: input.expectedRevision }) }
  );
}

export function suspendClientProductEntitlement(input: ProductEntitlementMutationInput) {
  return mutateClientProductEntitlement("suspendClientProductEntitlement", "suspend", input);
}

export function revokeClientProductEntitlement(input: ProductEntitlementMutationInput) {
  return mutateClientProductEntitlement("revokeClientProductEntitlement", "revoke", input);
}

export function listReleasePublicationRequests(params = new URLSearchParams()) {
  const query = params.toString();
  return adminApiRequest<ListResponse<ReleasePublicationRequestRecord>>(
    "listReleasePublicationRequests",
    `/admin/v1/release-publication-requests${query ? `?${query}` : ""}`
  );
}

export function createApprovedReleasePublication(manifest: ReleasePublicationManifest) {
  return adminApiRequest<ReleasePublicationRequestRecord>(
    "createApprovedReleasePublication",
    "/admin/v1/release-publication-requests",
    { method: "POST", body: JSON.stringify(manifest) }
  );
}

export function resolveReleasePublicationRequest(requestCode: string) {
  return adminApiRequest<ReleasePublicationRequestRecord>(
    "resolveReleasePublicationRequest",
    `/admin/v1/release-publication-requests/${encodeURIComponent(requestCode)}`
  );
}

export function approveReleasePublicationRequest(requestCode: string, expectedRevision: number) {
  return adminApiRequest<ReleasePublicationRequestRecord>(
    "approveReleasePublicationRequest",
    `/admin/v1/release-publication-requests/${encodeURIComponent(requestCode)}/approve`,
    { method: "POST", body: JSON.stringify({ expectedRevision }) }
  );
}

export function rejectReleasePublicationRequest(requestCode: string, expectedRevision: number, reason: string) {
  return adminApiRequest<ReleasePublicationRequestRecord>(
    "rejectReleasePublicationRequest",
    `/admin/v1/release-publication-requests/${encodeURIComponent(requestCode)}/reject`,
    { method: "POST", body: JSON.stringify({ expectedRevision, reason }) }
  );
}

export function saveApp(input: SaveAppInput) {
  return adminApiRequest<AppRecord>("saveApp", "/admin/v1/apps", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function listReleases(params: URLSearchParams) {
  const query = params.toString();
  return adminApiRequest<ListResponse<ReleaseRecord>>("listReleases", `/admin/v1/releases${query ? `?${query}` : ""}`);
}

export function createRelease(input: CreateReleaseInput) {
  return adminApiRequest<ReleaseRecord>("createRelease", "/admin/v1/releases", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function deleteRelease(releaseId: string) {
  return adminApiRequest<ReleaseRecord>("deleteRelease", `/admin/v1/releases/${releaseId}`, {
    method: "DELETE"
  });
}

export function listReleaseArtifacts(releaseId: string) {
  return adminApiRequest<ListResponse<ReleaseArtifactRecord>>("listReleaseArtifacts", `/admin/v1/releases/${releaseId}/artifacts`);
}

export function listReleaseDownloadGrants(params: URLSearchParams) {
  const query = params.toString();
  return adminApiRequest<ListResponse<ReleaseDownloadGrantRecord>>("listReleaseDownloadGrants", `/admin/v1/release-download-grants${query ? `?${query}` : ""}`);
}

export function createReleaseDownloadGrant(input: CreateReleaseDownloadGrantInput) {
  return adminApiRequest<IssuedReleaseDownloadGrantRecord>("createReleaseDownloadGrant", "/admin/v1/release-download-grants", {
    method: "POST",
    body: JSON.stringify({
      artifactId: input.artifactId,
      clientId: input.clientId,
      ttlHours: input.ttlHours,
      maxDownloads: input.maxDownloads,
      note: input.note
    })
  });
}

export function revokeReleaseDownloadGrant(input: RevokeReleaseDownloadGrantInput) {
  return adminApiRequest<ReleaseDownloadGrantRecord>("revokeReleaseDownloadGrant", `/admin/v1/release-download-grants/${input.grantId}/revoke`, {
    method: "POST",
    body: JSON.stringify({ reason: input.reason })
  });
}

export function uploadReleaseArtifact(input: UploadReleaseArtifactInput) {
  const form = new FormData();
  form.set("os", input.os);
  form.set("arch", input.arch);
  form.set("kind", input.kind);
  form.set("defaultDownload", String(input.defaultDownload));
  form.set("signingState", input.signingState);
  if (input.webview2Strategy) {
    form.set("webview2Strategy", input.webview2Strategy);
  }
  form.set("file", input.file);
  return adminApiRequest<ReleaseArtifactRecord>("uploadReleaseArtifact", `/admin/v1/releases/${input.releaseId}/artifacts`, {
    method: "POST",
    body: form
  });
}

export function deleteReleaseArtifact(input: DeleteReleaseArtifactInput) {
  return adminApiRequest<ReleaseArtifactRecord>("deleteReleaseArtifact", `/admin/v1/releases/${input.releaseId}/artifacts/${input.artifactId}`, {
    method: "DELETE"
  });
}

export function publishRelease(releaseId: string) {
  return adminApiRequest<ReleaseRecord>("publishRelease", `/admin/v1/releases/${releaseId}/publish`, {
    method: "POST"
  });
}

export function yankRelease(input: YankReleaseInput) {
  return adminApiRequest<ReleaseRecord>("yankRelease", `/admin/v1/releases/${input.releaseId}/yank`, {
    method: "POST",
    body: JSON.stringify({ reason: input.reason })
  });
}

export function restoreRelease(input: RestoreReleaseInput) {
  return adminApiRequest<ReleaseRecord>("restoreRelease", `/admin/v1/releases/${input.releaseId}/restore`, {
    method: "POST",
    body: JSON.stringify({ reason: input.reason })
  });
}

export function listEnrollmentTokens(params: URLSearchParams) {
  const query = params.toString();
  return adminApiRequest<ListResponse<EnrollmentTokenRecord>>("listEnrollmentTokens", `/admin/v1/enrollment-tokens${query ? `?${query}` : ""}`);
}

export function issueEnrollmentToken(input: IssueEnrollmentTokenInput) {
  return adminApiRequest<IssuedEnrollmentTokenRecord>("issueEnrollmentToken", "/admin/v1/enrollment-tokens", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function resolveRegistrationRequest(input: RegistrationRequestResolveInput) {
  return adminApiRequest<RegistrationRequestResolveRecord>("resolveRegistrationRequest", "/admin/v1/registration-requests/resolve", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export function getRegistrationRequest(requestId: string) {
  return adminApiRequest<RegistrationRequestRecord>("getRegistrationRequest", `/admin/v1/registration-requests/${requestId}`);
}

export function approveRegistrationRequest(input: RegistrationRequestApprovalInput) {
  return adminApiRequest<RegistrationRequestRecord>(
    "approveRegistrationRequest",
    `/admin/v1/registration-requests/${encodeURIComponent(input.requestId)}/approve`,
    {
      method: "POST",
      body: JSON.stringify({
        mutationId: crypto.randomUUID(),
        expectedRevision: input.expectedRevision,
        clientId: input.clientId,
        stationNote: input.stationNote?.trim() ?? ""
      })
    }
  );
}

export function listStations(params: URLSearchParams) {
  const query = params.toString();
  return adminApiRequest<ListResponse<StationRecord>>("listStations", `/admin/v1/stations${query ? `?${query}` : ""}`);
}

export function getStation(stationId: string) {
  return adminApiRequest<StationRecord>("getStation", `/admin/v1/stations/${stationId}`);
}

export function getStationLicenseState(stationId: string) {
  return adminApiRequest<StationLicenseStateRecord>("getStationLicenseState", `/admin/v1/stations/${stationId}/license-state`);
}

export function listStationCerts(stationId: string) {
  return adminApiRequest<ListResponse<CertRecord>>("listStationCerts", `/admin/v1/stations/${stationId}/certs?limit=10`);
}

export function listStationOverrides(stationId: string) {
  return adminApiRequest<ListResponse<StationOverrideRecord>>("listStationOverrides", `/admin/v1/stations/${stationId}/overrides?limit=10`);
}

export function issueStationOverride(input: IssueStationOverrideInput) {
  return adminApiRequest<StationOverrideRecord>("issueStationOverride", `/admin/v1/stations/${input.stationId}/overrides`, {
    method: "POST",
    body: JSON.stringify({ reason: input.reason })
  });
}

export function decommissionStation(input: DecommissionStationInput) {
  return adminApiRequest<StationRecord>("decommissionStation", `/admin/v1/stations/${input.stationId}/decommission`, {
    method: "POST",
    body: JSON.stringify({ reason: input.reason })
  });
}

export function adminBanStation(input: StationAdminControlInput) {
  return adminApiRequest<StationRecord>("adminBanStation", `/admin/v1/stations/${input.stationId}/ban`, {
    method: "POST",
    body: JSON.stringify({ reason: input.reason, validUntil: input.validUntil })
  });
}

export function adminAllowStation(input: StationAdminControlInput) {
  return adminApiRequest<StationRecord>("adminAllowStation", `/admin/v1/stations/${input.stationId}/allow`, {
    method: "POST",
    body: JSON.stringify({ reason: input.reason, validUntil: input.validUntil })
  });
}

export function clearAdminBanStation(input: ClearStationAdminControlInput) {
  return adminApiRequest<StationRecord>("clearAdminBanStation", `/admin/v1/stations/${input.stationId}/ban`, {
    method: "DELETE",
    body: JSON.stringify({ reason: input.reason })
  });
}

export function clearAdminAllowStation(input: ClearStationAdminControlInput) {
  return adminApiRequest<StationRecord>("clearAdminAllowStation", `/admin/v1/stations/${input.stationId}/allow`, {
    method: "DELETE",
    body: JSON.stringify({ reason: input.reason })
  });
}

export function updateStationPolicy(input: UpdateStationPolicyInput) {
  return adminApiRequest<StationRecord>("updateStationPolicy", `/admin/v1/stations/${input.stationId}/policy`, {
    method: "PATCH",
    body: JSON.stringify({
      reason: input.reason,
      notes: input.notes,
      releaseChannelOverride: input.releaseChannelOverride,
      thresholdDaysOverride: input.thresholdDaysOverride,
      warningDaysOverride: input.warningDaysOverride,
      offlineGraceDaysOverride: input.offlineGraceDaysOverride,
      readonlyGraceDaysOverride: input.readonlyGraceDaysOverride
    })
  });
}

export function listStationAudit(stationId: string) {
  return adminApiRequest<ListResponse<AuditRecord>>("listStationAudit", `/admin/v1/stations/${stationId}/audit?limit=20`);
}

export function listAudit(params: URLSearchParams) {
  const query = params.toString();
  return adminApiRequest<ListResponse<AuditRecord>>("listAudit", `/admin/v1/audit${query ? `?${query}` : ""}`);
}

export function getRolloutReadinessReport(params: URLSearchParams) {
  const query = params.toString();
  return adminApiRequest<RolloutReadinessReportRecord>("getRolloutReadinessReport", `/admin/v1/reports/rollout-readiness${query ? `?${query}` : ""}`);
}

export function getOverrideUsageReport(params: URLSearchParams) {
  const query = params.toString();
  return adminApiRequest<OverrideUsageReportRecord>("getOverrideUsageReport", `/admin/v1/reports/override-usage${query ? `?${query}` : ""}`);
}

export function getPublicKey() {
  return adminApiRequest<PublicKeyRecord>("getPublicKey", "/admin/v1/keys/public");
}
